<template>
  <div
    class="dashboard-kpi-card"
    :class="[
      clickable && 'dashboard-kpi-card--clickable',
      category && 'dashboard-kpi-card--' + category,
      dimmed && 'dashboard-kpi-card--dimmed',
    ]"
    @click="clickable ? $emit('click') : null"
  >
    <v-tooltip v-if="tooltip" bottom max-width="420" content-class="kpi-tooltip">
      <template #activator="{ on }">
        <v-icon size="16" color="#6b7280" class="kpi-info-icon" v-on="on">mdi-information-variant</v-icon>
      </template>
      <div class="kpi-tooltip-inner">
        <v-icon size="16" color="#9ca3af" class="kpi-tooltip-icon">mdi-information</v-icon>
        <span>{{ tooltip }}</span>
      </div>
    </v-tooltip>
    <div class="kpi-top">
      <span class="kpi-label">{{ label }} <slot name="label-append" /></span>
      <v-icon :color="iconColor" size="20">{{ icon }}</v-icon>
    </div>
    <div class="kpi-body">
      <span class="kpi-value" :class="{'kpi-value--danger': danger}">
        {{ formattedValue }}
        <span v-if="unit" class="kpi-unit">{{ unit }}</span>
      </span>
      <div class="kpi-change" v-if="change !== null && change !== undefined">
        <span
          class="kpi-change-badge"
          :class="changeBadgeClass"
        >
          <v-icon x-small>{{ changeIcon }}</v-icon>
          {{ formattedChange }}
        </span>
        <span class="kpi-change-vs">{{ $t('org-dashboard.vs-previous') }}</span>
      </div>
      <div class="kpi-change" v-else-if="badge">
        <span class="kpi-badge-neutral">{{ badge }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DashboardKpiCard',
  props: {
    label: { type: String, required: true },
    value: { type: [Number, String], required: true },
    unit: { type: String, default: null },
    icon: { type: String, default: 'mdi-chart-box-outline' },
    iconColor: { type: String, default: '#10B981' },
    change: { type: Number, default: null },
    changeInverted: { type: Boolean, default: false },
    clickable: { type: Boolean, default: false },
    danger: { type: Boolean, default: false },
    badge: { type: String, default: null },
    category: { type: String, default: null },
    dimmed: { type: Boolean, default: false },
    tooltip: { type: String, default: null },
  },

  computed: {
    formattedValue() {
      if (typeof this.value === 'number') {
        if (this.value >= 1000) {
          return this.value.toLocaleString()
        }
        return this.value
      }
      return this.value
    },

    formattedChange() {
      if (this.change === null || this.change === undefined) return ''
      const abs = Math.abs(this.change)
      return typeof this.change === 'number' && !Number.isInteger(this.change)
        ? abs.toFixed(1) + '%'
        : abs + (typeof this.change === 'number' ? '%' : '')
    },

    isPositiveChange() {
      if (this.changeInverted) return this.change < 0
      return this.change > 0
    },

    changeIcon() {
      return this.change > 0 ? 'mdi-arrow-up' : 'mdi-arrow-down'
    },

    changeBadgeClass() {
      if (this.change === 0) return 'kpi-change-badge--neutral'
      return this.isPositiveChange ? 'kpi-change-badge--positive' : 'kpi-change-badge--negative'
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-kpi-card {
  position: relative;
  background: white;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  }

  &--clickable {
    cursor: pointer;
  }

  &--critical {
    border-left: 3px solid #EF4444;
  }

  &--usage {
    border-left: 3px solid #3B82F6;
  }

  &--strategic {
    border-left: 3px solid #8B5CF6;
  }

  &--dimmed {
    opacity: 0.55;

    .kpi-value {
      color: #10B981;
    }

    &:hover {
      opacity: 1;
    }
  }
}

.kpi-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.kpi-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.kpi-info-icon {
  position: absolute;
  bottom: 6px;
  right: 6px;
  opacity: 0.55;
  cursor: help;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.9;
  }
}

.kpi-body {
  display: flex;
  flex-direction: column;
}

.kpi-value {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;

  &--danger {
    color: #EF4444;
  }
}

.kpi-unit {
  font-size: 14px;
  font-weight: 400;
  color: #6b7280;
}

.kpi-change {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
}

.kpi-change-badge {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 500;
  padding: 1px 4px;
  border-radius: 3px;
  gap: 1px;

  &--positive {
    color: #059669;
    background: #ecfdf5;
  }

  &--negative {
    color: #dc2626;
    background: #fef2f2;
  }

  &--neutral {
    color: #6b7280;
    background: #f3f4f6;
  }
}

.kpi-change-vs {
  font-size: 10px;
  color: #9ca3af;
}

.kpi-badge-neutral {
  font-size: 10px;
  font-weight: 500;
  color: #6b7280;
  background: #f3f4f6;
  padding: 1px 6px;
  border-radius: 3px;
}
</style>

<style lang="scss">
.kpi-tooltip.v-tooltip__content {
  background: white !important;
  color: #374151 !important;
  border: 1px solid #e5e7eb;
  border-left: 3px solid #9ca3af;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  padding: 10px 14px;
  font-size: 12.5px;
  line-height: 1.5;
  opacity: 1 !important;

  .kpi-tooltip-inner {
    display: flex;
    align-items: flex-start;
    gap: 8px;

    .kpi-tooltip-icon {
      flex-shrink: 0;
      margin-top: 1px;
    }
  }
}
</style>
